import React, { useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Paper, Typography, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';

export interface DelayReason {
  reason: string;
  count: number;
  avgMinutes?: number | null;
  breakdown?: { reason: string; count: number }[];
}

interface DelaysBreakdownProps {
  reasons: DelayReason[];
  /** Fixed chart height in px. When set, skips aspect-ratio sizing so wide panels stay short. */
  height?: number;
}

const COLORS = [
  '#2B3C87',
  '#74AC48',
  '#B7DD9A',
  '#A6AFD9',
  '#6A78B6',
  '#485796',
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#96CEB4',
  '#FFEAA7',
  '#DDA0DD',
  '#98D8C8',
  '#F7DC6F',
  '#BB8FCE',
];

const CustomDelayTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const avgMinutes = data.payload?.avgMinutes;
    const breakdown = data.payload?.breakdown as { reason: string; count: number }[] | undefined;
    return (
      <Paper>
        <Box p={2}>
          <Typography gutterBottom>{data.name}</Typography>
          <Box display="flex" flexDirection="column">
            <Typography variant="h4">{`${data.value} delay${data.value !== 1 ? 's' : ''}`}</Typography>
            {avgMinutes != null && isFinite(avgMinutes) && (
              <Typography variant="body2" color="textSecondary">
                Avg {avgMinutes.toFixed(1)} min
              </Typography>
            )}
            {breakdown && breakdown.length > 0 && (
              <Box mt={1} style={{ maxHeight: 180, overflowY: 'auto' }}>
                {breakdown.map(item => (
                  <Box
                    key={item.reason}
                    display="flex"
                    justifyContent="space-between"
                    style={{ gap: 16, minWidth: '16ch' }}
                  >
                    <Typography variant="body2" color="textSecondary" style={{ overflowWrap: 'anywhere' }}>
                      {item.reason}
                    </Typography>
                    <Typography variant="body2">{item.count}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    );
  }
  return null;
};

const DelayLegend = ({ items }: { items: { name: string; color: string }[] }) => (
  <Box display="flex" flexWrap="wrap" style={{ gap: '4px 10px' }}>
    {items.map(item => (
      <Box key={item.name} display="flex" alignItems="center" style={{ gap: 5, minWidth: 0, maxWidth: '100%' }}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            backgroundColor: item.color,
            borderRadius: '50%',
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" title={item.name} style={{ lineHeight: 1.25 }}>
          {item.name}
        </Typography>
      </Box>
    ))}
  </Box>
);

const DelaysBreakdown = (props: DelaysBreakdownProps) => {
  const theme = useTheme();
  const totalCount = props.reasons.reduce((acc, cur) => acc + cur.count, 0);
  const title = String(totalCount);
  const subtitle = totalCount === 1 ? 'Delay' : 'Delays';
  const chartHeight = props.height;

  const [activeIndex, setActiveIndex] = useState<number | undefined>();

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(undefined);
  };

  const data = props.reasons.map((reason, index) => ({
    name: reason.reason,
    value: reason.count,
    avgMinutes: reason.avgMinutes,
    breakdown: reason.breakdown,
    color: COLORS[index % COLORS.length],
  }));

  const frameHeight = chartHeight || 280;

  if (!props.reasons || props.reasons.length === 0 || totalCount === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={frameHeight}
        style={{ color: theme.palette.text.secondary }}
      >
        <Typography variant="h2" style={{ marginBottom: 8 }}>
          0
        </Typography>
        <Typography variant="body2">No Delays</Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height={frameHeight} minWidth={0}>
      <Box flex={1} minHeight={0} minWidth={0}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="52%"
              outerRadius="78%"
              fill="#8884d8"
              stroke="none"
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              paddingAngle={4}
              cornerRadius={2}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
              activeIndex={activeIndex}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke={activeIndex === index ? '#ffffff' : 'none'}
                  strokeWidth={activeIndex === index ? 2 : 0}
                />
              ))}
            </Pie>
            <text
              x="50%"
              y="46%"
              fontSize={22}
              fontWeight={400}
              fill="white"
              textAnchor="middle"
              dominantBaseline="auto"
            >
              {title}
            </text>
            <text
              x="50%"
              y="50%"
              fontSize={12}
              fontWeight={500}
              fill="rgb(125	163	206)"
              textAnchor="middle"
              dominantBaseline="hanging"
            >
              {subtitle}
            </text>
            <Tooltip
              content={({ active, payload }) => <CustomDelayTooltip active={active} payload={payload} />}
              cursor={{ fill: 'rgba(255,255,255,0.15)' }}
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 4,
                border: 0,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Box flexShrink={0} maxHeight={88} overflow="auto" pt={0.5}>
        <DelayLegend items={data} />
      </Box>
    </Box>
  );
};

export default DelaysBreakdown;
